import {
  applyMiqDiff,
  getMiqSyncData,
  saveMiqDiscardedChanges,
} from "../glQueries";
import { postData } from "../glAxios";
import { MiqDeltaInput, MiqFieldDifference, MiqSyncData } from "../../types/miq/miq-diff";
import { MiqDiscardedChange, MiqDiscardedChangeInput } from "../../types/miq/miq-discarded-change";
import { MiqFieldUpdate } from "../../utils/miq/miq-sync-mapper";

export interface MiqDiscardedFields {
  [fieldPath: string]: string | undefined;
}

export interface MiqSyncFetchResult {
  deltas: MiqDeltaInput[] | null;
  discardedFields: MiqDiscardedFields;
}

// fetches both the raw MIQ deltas and the previously discarded fields for a listing in one call
export const fetchMiqSyncData = (listingId: number): Promise<MiqSyncFetchResult> => {
  return postData(getMiqSyncData(listingId)).then((response) => {
    if (
      response &&
      Array.isArray(response.errors) &&
      response.errors.length > 0
    ) {
      // surfaced so a backend failure (e.g. bad MIQ data) isn't mistaken for "no updates"
      console.error("getMiqSyncData returned errors", response.errors);
    }

    const syncData: MiqSyncData | undefined =
      response && response.data && response.data.getMiqSyncData;

    const deltas =
      syncData && Array.isArray(syncData.currentData)
        ? (syncData.currentData as MiqFieldDifference[])
            .map((difference) => ({
              jsonPath: difference.fieldPath,
              deValue: difference.currentValue,
              newValue: difference.edpValue,
            }))
            .filter(
              (delta) =>
                typeof delta.jsonPath === "string" &&
                delta.jsonPath.trim().length > 0,
            )
        : null;

    const discardedFields = (
      syncData && Array.isArray(syncData.discardedData)
        ? syncData.discardedData
        : []
    ).reduce((result: MiqDiscardedFields, change: MiqDiscardedChange) => {
      if (change && change.fieldPath) {
        result[change.fieldPath] = change.miqValue;
      }

      return result;
    }, {});

    return { deltas, discardedFields };
  });
};

export const saveDiscardedFields = async (
  listingId: string,
  updates: MiqFieldUpdate[],
  consideredFieldPaths: string[],
): Promise<boolean> => {
  const changes: MiqDiscardedChangeInput[] = updates
    .filter(
      (update) =>
        update.delta &&
        typeof update.delta.jsonPath === "string" &&
        update.delta.jsonPath.trim().length > 0,
    )
    .map((update) => ({
      fieldPath: update.delta.jsonPath,
      currentValue: update.delta.deValue,
      miqValue: update.delta.newValue,
    }));

  const response = await postData(
    saveMiqDiscardedChanges(Number(listingId), changes, consideredFieldPaths),
  );
  if (
    response &&
    Array.isArray(response.errors) &&
    response.errors.length > 0
  ) {
    throw new Error(
      response.errors[0].message || "Unable to save discarded MIQ changes.",
    );
  }
  const saved =
    response && response.data && response.data.saveMiqDiscardedChanges === true;
  if (!saved) {
    throw new Error("The discarded MIQ changes were not saved.");
  }
  return true;
};

export const applyMiqDiffChanges = async (
  listingId: number,
  deltas: MiqDeltaInput[],
): Promise<void> => {
  const response = await postData(applyMiqDiff(listingId, deltas));

  if (
    response &&
    Array.isArray(response.errors) &&
    response.errors.length > 0
  ) {
    throw new Error(
      response.errors[0].message || "Unable to apply MIQ updates.",
    );
  }

  if (!response || !response.data || !response.data.applyMiqDiff) {
    throw new Error("The MIQ updates could not be applied.");
  }
};
